// What the facilitator sets per household from the panel. Each value picks the outcome the household meets the next time
// they reach that point: a processor result, BOB capacity, a supplier answer. None of them is chosen by chance, and none
// is a live operation. Staff actions on individual records (technician results, case owners) are separate (ledger.ts).
export interface Scenario {
  start: 'ui' | 'chat';                                   // starting concept (the research protocol alternates by household)
  wait: 'seconds' | 'held';                               // preparation: the quote follows in seconds, or waits for the prototype control
  assistant: 'live' | 'scripted';                         // scripted answers stand in for the model
  service: 'up' | 'down';                                 // down shows "Roundstead is unavailable right now"
  area: 'served' | 'not_served';                          // eligibility for this address
  intake: 'open' | 'restricted';                          // F9 admission limit: new joining paused; existing members keep their care
  terms: 'v1' | 'v2';                                     // v2: issued terms change (same approved prices) and must be reviewed again
  payment: 'confirmed' | 'processing' | 'failed' | 'unknown';
  activation: 'active' | 'pending' | 'problem';
  capacity: 'windows' | 'none';                           // F2: actual windows offered for a requested visit, or none yet
  booking: 'confirm' | 'taken';                           // F2: a chosen window is accepted, or was taken before confirmation
  person: 'requested' | 'accepted' | 'unavailable';       // a new request for a person
  deals: 'none' | 'available';                            // F5: no supplier deal available now, or one optional deal
  supplier: 'accepts' | 'refuses';                        // F5: the supplier's answer to an authorized deal order
  info: 'pending' | 'enforced';                           // F6: how a changed information choice is reported downstream
  export: 'available' | 'failed';                         // F6: generated export is usable, or generated but inaccessible
  renewal: 'not_offered' | 'offered';                     // F7: the next-term offer exists, or not yet
  billing: 'stop_pending' | 'stop_confirmed';             // F7: after a leave request, whether billing stop is verified
}

export const DEFAULT_SCENARIO: Scenario = {
  start: 'ui', wait: 'seconds', assistant: 'live', service: 'up', area: 'served', intake: 'open', terms: 'v1', payment: 'confirmed',
  activation: 'active', capacity: 'windows', booking: 'confirm', person: 'requested', deals: 'none', supplier: 'accepts', info: 'pending',
  export: 'available', renewal: 'not_offered', billing: 'stop_pending',
};

export const SCENARIO_OPTIONS: { [K in keyof Scenario]: Scenario[K][] } = {
  start: ['ui', 'chat'], wait: ['seconds', 'held'], assistant: ['live', 'scripted'], service: ['up', 'down'], area: ['served', 'not_served'],
  intake: ['open', 'restricted'], terms: ['v1', 'v2'], payment: ['confirmed', 'processing', 'failed', 'unknown'], activation: ['active', 'pending', 'problem'],
  capacity: ['windows', 'none'], booking: ['confirm', 'taken'], person: ['requested', 'accepted', 'unavailable'], deals: ['none', 'available'],
  supplier: ['accepts', 'refuses'], info: ['pending', 'enforced'], export: ['available', 'failed'], renewal: ['not_offered', 'offered'],
  billing: ['stop_pending', 'stop_confirmed'],
};

/** Keeps only known keys and allowed values. */
export function mergeScenario(base: Scenario, patch: Record<string, unknown>): Scenario {
  const out = { ...DEFAULT_SCENARIO, ...base };
  for (const k of Object.keys(SCENARIO_OPTIONS) as (keyof Scenario)[]) {
    const v = patch[k];
    if (typeof v === 'string' && (SCENARIO_OPTIONS[k] as string[]).includes(v)) (out as Record<string, string>)[k] = v;
  }
  return out;
}
